import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import {
  Briefcase, Sparkle, Robot, ArrowsClockwise, WarningCircle, MapPin,
  ArrowSquareOut, ArrowLeft, MagnifyingGlass, LockSimple,
} from "@phosphor-icons/react";

/**
 * Vacatures — AI-gegenereerde Nederlandse vacatures via Perplexity Sonar Pro.
 * Combineert alle tests/profielen + CV + breintype als context.
 * Max 3 zoekopdrachten per coachee. Work coach kan resetten.
 * Alleen meest recente zoekopdracht wordt bewaard.
 */
export default function CoacheeVacaturesPage() {
  const [coachee, setCoachee] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const pollRef = React.useRef(null);

  // Register with the global AI-task registry so the floating pill stays
  // visible even when the user navigates away from /vacatures. `coachee?.id`
  // is empty on first render so the guard also covers that.
  useAITaskRegistration({
    active: searching && !!coachee,
    id: coachee ? `vacatures-${coachee.id}` : null,
    label: "Vacatures zoeken via Perplexity",
    returnTo: "/vacatures",
  });

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const startPolling = (coacheeId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: fresh } = await api.get(`/coachees/${coacheeId}/vacatures`);
        setDoc(fresh);
        if (fresh.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSearching(false);
          if (fresh.status === "failed") {
            setError(fresh.error || "De AI kon geen vacatures vinden. Probeer het opnieuw.");
          }
        }
      } catch (err) {
        console.warn("Vacatures poll error:", err?.message || err);
      }
    }, 4000);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (!own) { setLoading(false); return; }
        const { data } = await api.get(`/coachees/${own.id}/vacatures`);
        setDoc(data);
        if (data.status === "running") {
          setSearching(true);
          startPolling(own.id);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSearch = async () => {
    if (!coachee || searching) return;
    setError("");
    setSearching(true);
    setConfirmRegen(false);
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/vacatures/search`,
        {},
        { timeout: 30000 },
      );
      setDoc(data);
      if (data.status === "running") {
        startPolling(coachee.id);
      } else {
        setSearching(false);
        if (data.status === "failed") {
          setError(data.error || "De AI kon geen vacatures vinden.");
        }
      }
    } catch (err) {
      setSearching(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return <AppLayout title="Vacatures"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }
  if (searching) {
    return (
      <AppLayout title="Vacatures">
        <div className="max-w-2xl" data-testid="vac-ai-running">
          <div className="neuro-card-bold p-8 bg-white">
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex h-14 w-14 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm animate-pulse">
                <Robot size={30} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#1E88E5] mb-1">Even geduld</div>
                <h2 className="font-display text-xl font-bold tracking-tight">
                  De AI zoekt actuele vacatures voor je...
                </h2>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  We doorzoeken Nederlandse vacaturebanken (LinkedIn, Indeed, Werk.nl,
                  Nationale Vacaturebank, Monsterboard) op basis van je CV, breintype,
                  alle tests en je drie profielen. Dit duurt ongeveer 15-30 seconden.
                </p>
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                  💡 <strong>Je kunt gerust doorwerken.</strong> Klik in de zijbalk naar een
                  andere pagina — de AI werkt op de achtergrond door en de resultaten
                  staan hier klaar zodra je terugkomt.
                </p>
                <div className="mt-4 flex gap-1">
                  <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const used = doc?.ai_count ?? 0;
  const max = doc?.ai_max ?? 3;
  const remaining = doc?.ai_remaining ?? Math.max(0, max - used);
  const results = doc?.results || [];
  const hasResults = results.length > 0;
  const limitReached = remaining <= 0;

  return (
    <AppLayout title="Vacatures">
      <div className="max-w-4xl space-y-6 pb-12" data-testid="vacatures-page">
        {/* Intro */}
        <div className="neuro-card-bold p-5 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
              <Briefcase size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight">Vacatures op maat</h2>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                De AI doorzoekt het Nederlandse internet en levert je <strong>10 actuele
                vacatures</strong> die passen bij jouw CV, breintype, alle tests en je
                drie profielen. Klik op een vacature voor het volledige aanbod.
              </p>
              <div className="mt-3 text-xs text-zinc-600" data-testid="vac-counter">
                <strong>Zoekopdrachten: {used} van {max}</strong>
                {!limitReached
                  ? <> — nog <strong>{remaining}</strong> {remaining === 1 ? "keer" : "keer"} mogelijk.</>
                  : <> — limiet bereikt.</>}
                {doc?.generated_at && (
                  <span className="ml-2 text-zinc-400">
                    · laatst gezocht: {new Date(doc.generated_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="vac-error">
            <WarningCircle size={18} className="shrink-0 mt-0.5" /> <div>{error}</div>
          </div>
        )}

        {doc?.status === "failed" && !error && (
          <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="vac-failed">
            <Sparkle size={18} weight="duotone" className="shrink-0 mt-0.5 text-amber-600" />
            <div>De AI kon deze keer geen vacatures vinden. Probeer het zo opnieuw.</div>
          </div>
        )}

        {limitReached && (
          <div className="border-2 border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="vac-limit-reached">
            <LockSimple size={18} weight="duotone" className="shrink-0 mt-0.5 text-amber-600" />
            <div>
              Je hebt het maximum van <strong>{max} zoekopdrachten</strong> bereikt.
              <strong> Vraag je work coach om een reset</strong> als je een nieuwe
              zoekopdracht wilt starten.
            </div>
          </div>
        )}

        {/* Action */}
        {!hasResults ? (
          <div className="neuro-card p-6 bg-white text-center" data-testid="vac-empty">
            <MagnifyingGlass size={32} weight="duotone" className="text-[#1E88E5] mx-auto mb-3" />
            <p className="text-sm text-zinc-600 max-w-md mx-auto mb-4 leading-relaxed">
              Klik op de knop hieronder om de AI 10 actuele Nederlandse vacatures
              te laten zoeken. We gebruiken alles wat we van jou weten.
            </p>
            <button
              type="button"
              onClick={handleSearch}
              disabled={limitReached}
              data-testid="vac-search-btn"
              className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1565C0] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <Sparkle size={16} weight="duotone" /> Zoek 10 vacatures
            </button>
          </div>
        ) : (
          <>
            {/* Results */}
            <div className="space-y-3" data-testid="vac-results-list">
              {results.map((job, idx) => (
                <article
                  key={`${idx}-${job.url}`}
                  className="neuro-card-bold p-5 bg-white"
                  data-testid={`vac-result-${idx}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <span className="shrink-0 inline-flex h-8 w-8 items-center justify-center bg-[#1E88E5] text-white text-sm font-bold rounded-sm">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg font-bold tracking-tight">
                        {job.title}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-700">
                        <span className="font-medium">{job.company}</span>
                        {job.location && (
                          <span className="inline-flex items-center gap-1 text-zinc-500 text-xs">
                            <MapPin size={12} /> {job.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {job.fit_explanation && (
                    <p className="text-sm text-zinc-600 mt-3 leading-relaxed border-l-4 border-[#1E88E5]/30 pl-3">
                      {job.fit_explanation}
                    </p>
                  )}
                  <div className="mt-4">
                    <a
                      href={job.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`vac-result-link-${idx}`}
                      className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-3 py-1.5 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors"
                    >
                      Bekijk vacature <ArrowSquareOut size={14} weight="bold" />
                    </a>
                  </div>
                </article>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-end pt-2">
              <button
                type="button"
                onClick={() => setConfirmRegen(true)}
                disabled={limitReached}
                data-testid="vac-research-btn"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors disabled:border-zinc-300 disabled:text-zinc-400 disabled:hover:bg-white disabled:cursor-not-allowed"
              >
                <ArrowsClockwise size={14} weight="bold" /> Nieuwe zoekopdracht ({remaining} over)
              </button>
            </div>
          </>
        )}

        {confirmRegen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="vac-regen-modal" role="dialog" aria-modal="true">
            <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
              <div className="flex items-start gap-3 mb-3">
                <WarningCircle size={24} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
                <h3 className="font-display text-lg font-bold tracking-tight">Nieuwe zoekopdracht starten?</h3>
              </div>
              <ul className="text-sm text-zinc-700 leading-relaxed space-y-2 mb-3 list-disc pl-5">
                <li>De huidige resultaten worden <strong>overschreven</strong>.</li>
                <li>De AI gebruikt je actuele profielen en testresultaten.</li>
              </ul>
              <div className="text-xs text-zinc-500 mb-5">
                Na deze zoekopdracht heb je nog <strong>{Math.max(0, remaining - 1)}</strong> {Math.max(0, remaining - 1) === 1 ? "kans" : "kansen"} over.
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setConfirmRegen(false)} data-testid="vac-regen-cancel"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors">
                  Annuleer
                </button>
                <button type="button" onClick={handleSearch} data-testid="vac-regen-confirm"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#1E88E5] transition-colors inline-flex items-center gap-2">
                  <ArrowsClockwise size={14} weight="bold" /> Ja, zoek opnieuw
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => navigate("/profielen")}
            data-testid="vac-back"
            className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-300 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:border-zinc-900 transition-colors"
          >
            <ArrowLeft size={14} /> Terug naar profielen
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

function Dot({ delay }) {
  return <span className="inline-block w-2 h-2 rounded-full bg-[#1E88E5] animate-pulse" style={{ animationDelay: `${delay}ms` }} />;
}
